"use client";

import { c as _c } from "react/compiler-runtime";
import { useHierarchiesControllerGetAncestors } from "@dc/gateway-jsonapi";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { useEffect, useMemo, useRef } from "react";
import { useMe } from "@/hooks/useMe";
import { useTeamsContext } from "@/hooks/useTeamsContext";
import { useSetAuthenticatedLaunchDarklyContextReady } from "./LaunchDarklyProvider";

/**
 * Client-side component that updates LaunchDarkly context with organization and team info
 * This runs after QueryClient and TeamsProvider are available
 * Must be rendered inside Providers component
 */
export function LaunchDarklyContextUpdater() {
  const $ = _c(20);
  const ldClient = useLDClient();
  const setAuthenticatedContextReady = useSetAuthenticatedLaunchDarklyContextReady();
  const {
    me,
    userId,
    currentTeamId,
    isLoading: isLoadingUser
  } = useMe();
  const {
    currentTeam,
    currentScopeId,
    organizationScopeId
  } = useTeamsContext();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      teamId: null,
      userId: null,
      organizationId: null,
      organizationName: undefined
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const previousValuesRef = useRef(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      scopeType: "ORGANIZATION"
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = !!currentScopeId;
  let t3;
  if ($[2] !== t2) {
    t3 = {
      query: {
        enabled: t2
      }
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    data: ancestorsData,
    isLoading: isLoadingAncestors,
    isFetching: isFetchingAncestors
  } = useHierarchiesControllerGetAncestors(currentScopeId || "", t1, t3);
  let t4;
  bb0: {
    if (!ancestorsData?.data) {
      t4 = undefined;
      break bb0;
    }
    let t5;
    if ($[4] !== ancestorsData.data) {
      t5 = ancestorsData.data.find(_temp);
      $[4] = ancestorsData.data;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    const organizationScope = t5;
    const organization = organizationScope?.attributes?.organization;
    if (organization && typeof organization === "object" && "name" in organization) {
      t4 = (organization as {
        name?: string;
      }).name;
      break bb0;
    }
    t4 = undefined;
  }
  const organizationName = t4;
  let t5;
  let t6;
  if ($[6] !== currentScopeId || $[7] !== currentTeam || $[8] !== currentTeamId || $[9] !== isFetchingAncestors || $[10] !== isLoadingAncestors || $[11] !== isLoadingUser || $[12] !== ldClient || $[13] !== me || $[14] !== organizationName || $[15] !== organizationScopeId || $[16] !== setAuthenticatedContextReady || $[17] !== userId) {
    t5 = () => {
      if (!ldClient) {
        return;
      }
      if (!me) {
        if (!isLoadingUser) {
          setAuthenticatedContextReady?.(true);
        }
        return;
      }
      if (currentScopeId && (isLoadingAncestors || isFetchingAncestors)) {
        return;
      }
      const previousValues = previousValuesRef.current;
      const hasTeamChanged = previousValues.teamId !== currentTeamId;
      const hasUserChanged = previousValues.userId !== userId;
      const hasOrganizationChanged = previousValues.organizationId !== organizationScopeId || previousValues.organizationName !== organizationName;
      const hasIdentified = previousValues.userId !== null;
      if (hasIdentified && !hasTeamChanged && !hasUserChanged && !hasOrganizationChanged) {
        return;
      }
      const currentContext = ldClient.getContext() as {
        kind?: string;
        user?: {
          key: string;
          name?: string;
          email?: string;
          anonymous?: boolean;
        };
      };
      const userContext = {
        key: userId || currentContext.user?.key || ""
      };
      if (me?.data?.attributes?.firstName || me?.data?.attributes?.lastName) {
        const nameParts = [me.data.attributes.firstName, me.data.attributes.lastName].filter(Boolean);
        if (nameParts.length > 0) {
          userContext.name = nameParts.join(" ");
        }
      }
      if (me?.data?.attributes?.email) {
        userContext.email = me.data.attributes.email;
      }
      if (currentContext.user?.anonymous) {
        userContext.anonymous = true;
      }
      const updatedContext = {
        kind: "multi" as const,
        user: userContext
      };
      if (currentTeamId && currentTeam) {
        updatedContext.team = {
          key: currentTeamId,
          name: currentTeam.attributes?.name
        };
      }
      if (organizationScopeId) {
        updatedContext.organization = {
          key: organizationScopeId,
          ...(organizationName && {
            name: organizationName
          })
        };
      }
      const isTeamOnlyChange = hasIdentified && !hasUserChanged && !hasOrganizationChanged;
      if (!isTeamOnlyChange) {
        setAuthenticatedContextReady?.(false);
      }
      let cancelled = false;
      ldClient.identify(updatedContext).then(() => {
        if (cancelled) {
          return;
        }
        previousValuesRef.current = {
          teamId: currentTeamId,
          userId: userId || null,
          organizationId: organizationScopeId,
          organizationName
        };
        setAuthenticatedContextReady?.(true);
      }).catch(error => {
        if (cancelled) {
          return;
        }
        console.error("[LaunchDarkly] Failed to update context:", error);
        setAuthenticatedContextReady?.(true);
      });
      return () => {
        cancelled = true;
      };
    };
    t6 = [ldClient, me, userId, currentTeamId, currentTeam, currentScopeId, organizationScopeId, organizationName, isLoadingAncestors, isFetchingAncestors, isLoadingUser, setAuthenticatedContextReady];
    $[6] = currentScopeId;
    $[7] = currentTeam;
    $[8] = currentTeamId;
    $[9] = isFetchingAncestors;
    $[10] = isLoadingAncestors;
    $[11] = isLoadingUser;
    $[12] = ldClient;
    $[13] = me;
    $[14] = organizationName;
    $[15] = organizationScopeId;
    $[16] = setAuthenticatedContextReady;
    $[17] = userId;
    $[18] = t5;
    $[19] = t6;
  } else {
    t5 = $[18];
    t6 = $[19];
  }
  useEffect(t5, t6);
  return null;
}
function _temp(ancestor) {
  return ancestor.attributes?.type === "ORGANIZATION";
}